import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, RefreshCw, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import api, { formatApiError, tokenStore } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { safeReplace } from "@/lib/safeNavigate";

const CODE_LEN = 6;
const RESEND_COOLDOWN = 60;

/**
 * Email verification screen.
 *
 * Two entry paths:
 *   1. Registration → user is logged in but unverified → ProtectedRoute
 *      redirects here → they type the 6-digit code OR wait for auto-verify
 *      via the URL link (see VerifyEmailByToken below, mounted at
 *      /verify-email/:token which auto-submits and bounces here).
 *   2. Someone clicks the link in the email → /verify-email/:token → the
 *      route auto-calls the API with the token → on success sends them to
 *      /dashboard, on failure bounces to /onboarding/verify-email with a
 *      toast so they can enter the code manually.
 *
 * The 6-input UI supports paste (auto-splitting a 6-digit paste across boxes)
 * and Backspace to jump back a box. Every keystroke that fills the 6th box
 * auto-submits so the mobile-friendly UX doesn't require an extra "Verify" tap.
 */
export default function VerifyEmail() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  // Iter468 · Fix — Honour `?next=` so the Festival Landing basket
  // round-trip (Iter467) returns the signed-up organiser back to the
  // landing page with the basket re-opened, not to /festival/mine.
  // Falls back to the historic organiser-vs-coach split.
  const nextParam = params.get("next");
  const [digits, setDigits] = useState(Array(CODE_LEN).fill(""));
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputRefs = useRef([]);
  // Iter68 defensive hardening — mirror of the MediaConsent bouncedRef guard.
  // Prevents a churny `user` reference from re-firing the auto-nav after
  // verification completes.
  const bouncedRef = useRef(false);

  // If they land here already verified (e.g. after clicking the token link),
  // bounce back to the app immediately so they don't stare at an empty form.
  // Iter328 · Bug 9 fix — use `safeReplace` (hard-nav) instead of
  // React Router's `nav(..., {replace:true})` because the auth
  // refresh triggered post-verify races with the router state update
  // and can cause an insecure-history loop.
  useEffect(() => {
    if (bouncedRef.current) return;
    if (user?.email_verified) {
      bouncedRef.current = true;
      // Iter468 — Honour `?next=` on the already-verified short-circuit too.
      safeReplace(nextParam || "/dashboard");
    }
  }, [user, nextParam]);

  // Countdown tick for the resend button.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const setDigit = (i, val) => {
    const clean = (val || "").replace(/\D/g, "");
    if (!clean) {
      // Deletion path — clear the box, don't jump focus.
      const next = [...digits];
      next[i] = "";
      setDigits(next);
      return;
    }
    // If a full 6-digit paste happens into any box, spread it.
    if (clean.length >= CODE_LEN) {
      const spread = clean.slice(0, CODE_LEN).split("");
      setDigits(spread);
      inputRefs.current[CODE_LEN - 1]?.focus();
      return;
    }
    const next = [...digits];
    next[i] = clean[0];
    setDigits(next);
    if (i < CODE_LEN - 1) inputRefs.current[i + 1]?.focus();
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  };

  const submitCode = useCallback(async (codeOverride) => {
    const code = (codeOverride ?? digits.join("")).trim();
    if (code.length !== CODE_LEN) {
      toast.error("Please enter all 6 digits.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/verify-email", { code });
      toast.success("Email verified.");
      if (refresh) await refresh();
      // Iter329 · Bug 9 — Send festival organisers straight to their
      // festival home instead of /dashboard. `/dashboard` for a
      // pending-details organiser would previously bounce off the
      // isPendingPlayer guard (fixed in ProtectedRoute), then back
      // to /festival/mine via the organiser rule, producing a loop.
      // Landing on /festival/mine directly is a single-hop redirect
      // with no possibility of ping-pong.
      // Iter468 · Fix — `?next=` wins over the organiser default so
      // the Iter467 basket round-trip returns the organiser to the
      // Festival Landing with their basket re-opened.
      const defaultTarget = user?.is_festival_organiser ? "/festival/mine" : "/dashboard";
      safeReplace(nextParam || defaultTarget);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Verification failed");
      // Clear boxes for another attempt
      setDigits(Array(CODE_LEN).fill(""));
      inputRefs.current[0]?.focus();
    } finally {
      setSubmitting(false);
    }
  }, [digits, refresh, nav]);

  // Auto-submit when all 6 digits are populated (mobile-friendly).
  useEffect(() => {
    if (submitting) return;
    if (digits.every((d) => d.length === 1)) {
      submitCode();
    }
   
  }, [digits.join("")]);

  const resend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    try {
      await api.post("/auth/verify-email/resend");
      toast.success("New code sent — check your inbox.");
      setCooldown(RESEND_COOLDOWN);
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      // Backend returns "Please wait Ns before requesting another code." on 429.
      const m = /(\d+)s/.exec(detail);
      if (m) setCooldown(Number(m[1]));
      toast.error(formatApiError(detail) || "Could not resend");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-6 py-10 noise-bg" data-testid="verify-email-page">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 bg-[#CCFF00] flex items-center justify-center">
            <Mail strokeWidth={2.5} className="w-5 h-5 text-black" />
          </div>
          <div className="label-mono">EMAIL VERIFICATION</div>
        </div>

        <h1 className="h-display text-4xl sm:text-5xl mb-3 tracking-tighter">
          Check your inbox.
        </h1>
        <p className="text-sm text-[#A3A3A3] mb-8 leading-relaxed">
          We&apos;ve sent a 6-digit code to <span className="text-white font-mono">{user?.email}</span>.
          Enter it below, or click the link in the email — either works.
        </p>

        <div className="grid grid-cols-6 gap-2 mb-6" data-testid="verify-email-code-inputs">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              onPaste={(e) => {
                e.preventDefault();
                const p = (e.clipboardData?.getData("text") || "").replace(/\D/g, "");
                if (p) setDigit(i, p);
              }}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              disabled={submitting}
              data-testid={`verify-email-digit-${i}`}
              className="h-14 text-center text-2xl font-mono bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] outline-none disabled:opacity-50"
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-3">
          <button
            onClick={resend}
            disabled={cooldown > 0 || resending}
            className="text-[11px] uppercase tracking-wider text-[#A3A3A3] hover:text-[#CCFF00] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            data-testid="verify-email-resend"
          >
            {resending
              ? <><Loader2 className="w-3 h-3 animate-spin" /> Sending…</>
              : cooldown > 0
                ? `Resend in ${cooldown}s`
                : <><RefreshCw className="w-3 h-3" /> Resend code</>}
          </button>
          <button
            onClick={() => submitCode()}
            disabled={submitting || digits.some((d) => !d)}
            className="btn-volt px-5 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="verify-email-submit"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Verify
          </button>
        </div>

        <div className="mt-10 text-[10px] uppercase tracking-wider text-[#525252]">
          Wrong email?{" "}
          <button
            type="button"
            onClick={() => { logout?.(); nav("/register", { replace: true }); }}
            className="text-[#A3A3A3] hover:text-white underline uppercase tracking-wider text-[10px]"
            data-testid="verify-email-sign-out"
          >
            Sign out
          </button>{" "}
          and register again.
        </div>
      </div>
    </div>
  );
}

/**
 * Handles the clickable link in the email — /verify-email/:token.
 * Auto-submits the token then routes based on result. This route sits
 * OUTSIDE ProtectedRoute so it works for a user who's logged out on a
 * different device.
 */
export function VerifyEmailByToken() {
  const { token } = useParams();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("verifying"); // verifying | done | failed
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      if (!token) {
        setState("failed");
        toast.error("This verification link is invalid.");
        nav("/onboarding/verify-email", { replace: true });
        return;
      }
      // If we're already logged in as the pending user, hit the authed endpoint.
      // If not, we can't verify from here alone — send them to login.
      if (!user) {
        // Store token for later (in-memory) — for now, redirect to login with a note.
        toast("Sign in first to verify your email, then click the link again.");
        nav("/login", { replace: true });
        return;
      }
      try {
        await api.post("/auth/verify-email", { token });
        toast.success("Email verified.");
        if (refresh) await refresh();
        setState("done");
        nav("/dashboard", { replace: true });
      } catch (e) {
        setState("failed");
        toast.error(formatApiError(e.response?.data?.detail) || "Link expired or invalid — try the 6-digit code instead.");
        nav("/onboarding/verify-email", { replace: true });
      }
    })();
   
  }, [token]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grid place-items-center" data-testid="verify-email-by-token">
      <div className="text-center">
        <Loader2 className="w-6 h-6 text-[#CCFF00] animate-spin mx-auto mb-3" />
        <div className="label-mono text-[#A3A3A3]">
          {state === "verifying" ? "Verifying your email…" : state === "done" ? "Verified — one sec…" : "Redirecting…"}
        </div>
      </div>
    </div>
  );
}

/**
 * Handles the magic-login link — /login-magic/:token — sent when someone tries
 * to re-register with an existing email. Consumes the token, establishes a
 * session, then routes them into the app.
 */
export function MagicLogin() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/magic-login", { token });
        // Persist the tokens the same way login() does.
        try { tokenStore.store(data.access_token, data.refresh_token, true); } catch { /* noop */ }
        if (refresh) await refresh();
        toast.success("Signed in.");
        nav("/dashboard", { replace: true });
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Sign-in link expired.");
        nav("/login", { replace: true });
      }
    })();
   
  }, [token]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grid place-items-center">
      <div className="text-center">
        <Loader2 className="w-6 h-6 text-[#CCFF00] animate-spin mx-auto mb-3" />
        <div className="label-mono text-[#A3A3A3]">Signing you in…</div>
      </div>
    </div>
  );
}
